import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getImages } from '../api/apiClient';
import MasonryGrid from '../components/MasonryGrid';
import Seo from '../components/Seo';
import ContentModule from '../components/ContentModule';
import { SITE_URL } from '../seoConfig';
import { Loader2 } from 'lucide-react';

const BATCH_SIZE = 20;

/**
 * Latest Photos — paginated, SEO-friendly.
 * - Each /page/N URL renders only that page's batch (photos (N-1)*20 .. N*20)
 *   so search engines don't see duplicate content across paginated pages.
 * - JS "Load more" appends the next batch in-browser and uses pushState to
 *   reflect the new page in the address bar.
 */
const LatestPhotosPage = () => {
  const { page: pageParam } = useParams();
  const initialPage = Math.max(1, parseInt(pageParam, 10) || 1);

  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getImages({
          sort: 'latest',
          limit: BATCH_SIZE,
          skip: (initialPage - 1) * BATCH_SIZE,
        });
        if (cancelled) return;
        setImages(data.images || []);
        setTotal(data.total || 0);
        setCurrentPage(initialPage);
      } catch (err) {
        console.error('Error fetching latest photos:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceOffset = (currentPage - 1) * BATCH_SIZE + images.length;
  const hasMore = sourceOffset < total;
  const nextPage = currentPage + 1;
  const loadMoreHref = `/photos/page/${nextPage}`;

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const data = await getImages({
        sort: 'latest',
        limit: BATCH_SIZE,
        skip: sourceOffset,
      });
      setImages((prev) => [...prev, ...(data.images || [])]);
      setCurrentPage(nextPage);
      window.history.pushState({}, '', loadMoreHref);
    } catch (err) {
      console.error('Error loading more latest photos:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / BATCH_SIZE));

  // Page-level JSON-LD only (CollectionPage + BreadcrumbList). The photo grid is
  // dynamic (newest first) so individual photos are not listed in structured data.
  const pageUrl = currentPage > 1 ? `${SITE_URL}/photos/page/${currentPage}` : `${SITE_URL}/photos`;
  const collectionPageLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Latest Photos from Mauritius',
    url: pageUrl,
    description: 'The latest Mauritius photography from local photographers, newest first.',
    isPartOf: { '@type': 'WebSite', name: 'Explora Mauritius', url: SITE_URL },
  };
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Latest Photos', item: `${SITE_URL}/photos` },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={`Latest Mauritius Photos${currentPage > 1 ? ` — Page ${currentPage}` : ''}`}
        description={`Browse the latest high-resolution Mauritius photos from local photographers. ${total} photos, newest first.`}
        canonical={currentPage > 1 ? `${typeof window !== 'undefined' ? window.location.origin : ''}/photos/page/${currentPage}` : `${typeof window !== 'undefined' ? window.location.origin : ''}/photos`}
        noindex={currentPage > 1}
        image={images[0]?.image_urls?.full}
        jsonLd={[collectionPageLd, breadcrumbLd]}
      />
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200 py-8">
        <div className="max-w-full md:max-w-[70%] mx-auto px-4 md:px-4 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="latest-photos-h1">
            Latest Photos from Mauritius
          </h1>
          <p className="text-gray-600">
            {total} {total === 1 ? 'photo' : 'photos'} · newest first
            {totalPages > 1 && <span> · page {currentPage} of {totalPages}</span>}
          </p>
          <ContentModule name="photos" />
        </div>
      </div>

      {/* Images Grid */}
      <div className="py-8">
        <MasonryGrid
          images={images}
          showPhotographer={true}
          photographerClickable={true}
          hasMore={hasMore}
          onLoadMore={loadMore}
          loadingMore={loadingMore}
          loadMoreHref={loadMoreHref}
          prioritizeFirst={true}
        />
      </div>
    </div>
  );
};

export default LatestPhotosPage;
